import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Mail, User, Heart, Search, ChevronDown, LogOut, Bookmark, MapPin, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { LOGO_URL, COMPANY_INFO } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, favorites, savedSearches, deleteSearch } = useAuth();
  const navigate = useNavigate();

  // Helper function to format saved search for display
  const formatSearchLabel = (search) => {
    const parts = [];
    if (search.location && search.location !== 'all') {
      parts.push(search.location.charAt(0).toUpperCase() + search.location.slice(1).replace('-', ' '));
    }
    if (search.propertyType && search.propertyType !== 'all') {
      parts.push(search.propertyType);
    }
    if (search.beds && search.beds !== 'any') {
      parts.push(`${search.beds}+ beds`);
    }
    if (search.minPrice && search.minPrice !== 'none') {
      parts.push(`$${(parseInt(search.minPrice)/1000)}K+`);
    }
    return parts.length > 0 ? parts.join(', ') : 'All Properties';
  };

  // Execute a saved search by navigating with params
  const executeSavedSearch = (search) => {
    const params = new URLSearchParams();
    if (search.location && search.location !== 'all') params.set('location', search.location);
    if (search.propertyType && search.propertyType !== 'all') params.set('propertyType', search.propertyType);
    if (search.beds && search.beds !== 'any') params.set('beds', search.beds);
    if (search.baths && search.baths !== 'any') params.set('baths', search.baths);
    if (search.minPrice && search.minPrice !== 'none') params.set('minPrice', search.minPrice);
    if (search.maxPrice && search.maxPrice !== 'none') params.set('maxPrice', search.maxPrice);
    navigate(`/search?${params.toString()}`);
  };

  const navLinks = [
    { name: 'Buy', href: '/search', hasDropdown: true },
    { name: 'Sell', href: '/sell' },
    { name: 'Neighbourhoods', href: '/neighbourhoods' },
    { name: 'About Me', href: '/about' },
    { name: 'Mortgage Calculator', href: '/mortgage-calculator' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className="w-full">
      {/* Top Bar */}
      <div className="bg-black text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-3 text-sm">
          <div className="flex items-center gap-6 min-w-0 flex-1">
            <Link
              to="/contact"
              className="flex items-center gap-2 hover:text-red-500 transition-colors min-w-0"
              data-testid="topbar-email"
              title={`Contact Alexander — ${COMPANY_INFO.email}`}
            >
              <Mail size={14} className="flex-shrink-0" />
              {/* Show short label on tiny screens, full address on md+ */}
              <span className="sm:hidden text-xs">Email me</span>
              <span className="hidden sm:inline truncate text-sm">{COMPANY_INFO.email}</span>
            </Link>
          </div>
          <div className="text-gray-400 hidden md:block flex-shrink-0">
            {COMPANY_INFO.brokerage}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <span className="relative h-14 w-14 flex-shrink-0 rounded-full overflow-hidden bg-black ring-1 ring-white/10 group-hover:ring-red-500/40 transition-all">
                <img
                  src={LOGO_URL}
                  alt="Alexander Conti — Brantford Area Homes"
                  className="absolute inset-0 h-full w-full object-cover scale-[1.6] group-hover:scale-[1.7] transition-transform duration-300"
                />
              </span>
              <div className="hidden sm:block">
                <h1 className="text-white font-bold text-xl tracking-wide">BRANTFORD AREA HOMES</h1>
                <p className="text-red-500 text-xs tracking-widest italic">YOUR VISION, MY MISSION</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200 text-sm font-medium"
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* User Actions */}
            <div className="flex items-center gap-3">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="hidden lg:flex text-white hover:bg-white/10 gap-2">
                      <User size={18} />
                      <span className="hidden sm:inline">{user.name}</span>
                      <ChevronDown size={14} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem onClick={() => navigate('/favorites')}>
                      <Heart size={16} className="mr-2" />
                      Favorites ({favorites.length})
                    </DropdownMenuItem>
                    
                    {/* Saved Searches Submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Bookmark size={16} className="mr-2" />
                        Saved Searches ({savedSearches.length})
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="w-72">
                        {savedSearches.length === 0 ? (
                          <DropdownMenuLabel className="text-gray-500 font-normal text-sm py-3 px-2">
                            No saved searches yet. Save a search from the search page!
                          </DropdownMenuLabel>
                        ) : (
                          <>
                            {savedSearches.slice(0, 5).map((search) => (
                              <DropdownMenuItem 
                                key={search.id} 
                                className="flex items-center justify-between group cursor-pointer"
                              >
                                <div 
                                  className="flex items-center gap-2 flex-1"
                                  onClick={() => executeSavedSearch(search)}
                                >
                                  <MapPin size={14} className="text-red-500 flex-shrink-0" />
                                  <span className="truncate text-sm">{formatSearchLabel(search)}</span>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteSearch(search.id);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                                >
                                  <Trash2 size={12} className="text-red-500" />
                                </button>
                              </DropdownMenuItem>
                            ))}
                            {savedSearches.length > 5 && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/saved-searches')}>
                                  <Search size={14} className="mr-2" />
                                  View All Saved Searches
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600">
                      <LogOut size={16} className="mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                /* Login/Register buttons for desktop when not logged in */
                <div className="hidden lg:flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="text-white hover:bg-white/10">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                className="lg:hidden text-white hover:bg-white/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-gray-900 border-t border-gray-700">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Auth Links in Mobile Menu */}
              <div className="border-t border-gray-700 mt-4 pt-4">
                {user ? (
                  <>
                    <Link
                      to="/favorites"
                      className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Heart size={16} className="inline mr-2" />
                      Favorites ({favorites.length})
                    </Link>
                    <Link
                      to="/saved-searches"
                      className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Search size={16} className="inline mr-2" />
                      Saved Searches
                    </Link>
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/10 rounded-md transition-all"
                    >
                      <LogOut size={16} className="inline mr-2" />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-white/10 rounded-md transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User size={16} className="inline mr-2" />
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="block px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/10 rounded-md transition-all"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User size={16} className="inline mr-2" />
                      Create Account
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
